import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import {
  StepCheckIcon,
  RadioActiveIcon,
  RadioIdleIcon,
} from "../NewOrderDailyLimits/icons";
import { PlusIcon, CloseIcon } from "../NewOrderSelectApp/icons";
import { SunIcon, MonitorIcon, MoonIcon } from "../Header/icons";
import { TariffLabel } from "../TariffLabel";
import { ThemeMode } from "../Header/types";
import { NewOrderHeaderProps, NewOrderHeaderStep } from "./types";

const THEMES: {
  mode: ThemeMode;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { mode: "light", Icon: SunIcon },
  { mode: "system", Icon: MonitorIcon },
  { mode: "dark", Icon: MoonIcon },
];

const StepPillIcon: React.FC<{ step: NewOrderHeaderStep }> = ({ step }) => {
  if (step.state === "completed")
    return <StepCheckIcon className={styles.stepIcon} />;
  if (step.state === "active")
    return <RadioActiveIcon className={styles.stepIcon} />;
  return <RadioIdleIcon className={cs(styles.stepIcon, styles.stepIconIdle)} />;
};

/**
 * New-order flow header (Figma 1052:8958 / 560:103944): title + order-type
 * tag, overlapping progress pills (check = completed, radio = active), then
 * the wallet chip (tariff + balance + add fund, or a walletSlot with the
 * app's real balance card), optional theme switch and a close button. Shared
 * by every order-creation screen so entering fullscreen or switching steps
 * never moves it.
 */
export const NewOrderHeader: React.FC<NewOrderHeaderProps> = ({
  title = "New order",
  orderType,
  steps,
  tariffLabel,
  tariffLevel = "trial",
  tariffMeta,
  amount,
  onAddFund,
  walletSlot,
  theme = "light",
  onThemeChange,
  onClose,
  className,
}) => {
  const wallet =
    walletSlot ??
    (amount && (
      <div className={styles.wallet}>
        {tariffLabel && (
          <TariffLabel
            level={tariffLevel}
            label={tariffLabel}
            meta={tariffMeta ?? ""}
          />
        )}

        <span className={styles.amount}>
          <span className={styles.amountCurrency}>{amount.currency ?? "$"}</span>
          <span className={styles.amountWhole}>{amount.whole}</span>
          <span className={styles.amountFraction}>.{amount.fraction}</span>
        </span>

        <button
          type="button"
          className={styles.addFund}
          onClick={onAddFund}
          aria-label="Add funds"
        >
          <PlusIcon />
        </button>
      </div>
    ));

  return (
    <div className={cs(styles.topRow, className)}>
      <div className={styles.titleGroup}>
        <h1 className={styles.title}>{title}</h1>
        {orderType && <span className={styles.orderType}>{orderType}</span>}
      </div>

      <div className={styles.stepper} role="group" aria-label="Progress">
        {steps.map((step, index) => (
          <span
            key={step.label}
            className={cs(styles.stepPill, {
              [styles.stepPillOverlapped]: index < steps.length - 1,
              [styles.stepPillTucked]: index > 0,
              [styles.stepPillUpcoming]: step.state === "upcoming",
            })}
            style={{ zIndex: steps.length - index }}
          >
            <StepPillIcon step={step} />
            <span
              className={cs({
                [styles.stepPillActiveLabel]: step.state === "active",
              })}
            >
              {step.label}
            </span>
          </span>
        ))}
      </div>

      <div className={styles.rightCluster}>
        {(wallet || onThemeChange) && (
          <div className={styles.walletGroup}>
            {wallet}

            {onThemeChange && (
              <div className={styles.themeSwitch} role="group" aria-label="Theme">
                {THEMES.map(({ mode: themeMode, Icon }) => (
                  <button
                    key={themeMode}
                    type="button"
                    className={cs(styles.switchItem, {
                      [styles.active]: theme === themeMode,
                    })}
                    onClick={() => onThemeChange(themeMode)}
                    aria-pressed={theme === themeMode}
                    aria-label={themeMode}
                  >
                    <Icon />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

export default NewOrderHeader;
